export const GUIDED_DISCLOSURE_EVENT = 'guided-disclosure-state'

const GUIDED_HIGHLIGHT_COLOR = 'rgba(255, 200, 0, 0.2)'
const GUIDED_CHECKBOX_GLOW = '0 0 0 2px rgba(255, 200, 0, 0.7), 0 0 14px rgba(255, 200, 0, 0.55)'
const GUIDED_SIGNATURE_SELECTOR = '[data-guided-signature="true"]'
const GUIDED_REQUIRED_SELECTOR =
  'input[required], textarea[required], select[required], input[aria-required="true"], textarea[aria-required="true"], select[aria-required="true"], [data-guided-required="true"]'
const GUIDED_STEP_SELECTOR = `${GUIDED_REQUIRED_SELECTOR}, ${GUIDED_SIGNATURE_SELECTOR}`

type GuidedStepType = 'text' | 'checkbox' | 'select' | 'signature'

type GuidedStep = {
  element: HTMLElement
  label: string
  type: GuidedStepType
  complete: boolean
}

export type GuidedDisclosureState = {
  enabled: boolean
  complete: boolean
  totalSteps: number
  completedSteps: number
  currentStepNumber: number
  currentStepLabel: string
}

export type GuidedDisclosureController = {
  destroy: () => void
  goToNextRequired: () => void
  getState: () => GuidedDisclosureState
}

const getElementWindow = (element: Element) => {
  return element.ownerDocument?.defaultView ?? window
}

const isHtmlElement = (value: unknown): value is HTMLElement => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const maybeElement = value as Element
  const elementWindow = getElementWindow(maybeElement)

  if (!(maybeElement instanceof elementWindow.Element)) {
    return false
  }

  return maybeElement instanceof elementWindow.HTMLElement
}

const isHtmlInputElement = (value: unknown): value is HTMLInputElement => {
  if (!isHtmlElement(value)) {
    return false
  }

  const elementWindow = getElementWindow(value)
  return value instanceof elementWindow.HTMLInputElement
}

const isHtmlTextAreaElement = (value: unknown): value is HTMLTextAreaElement => {
  if (!isHtmlElement(value)) {
    return false
  }

  const elementWindow = getElementWindow(value)
  return value instanceof elementWindow.HTMLTextAreaElement
}

const isHtmlSelectElement = (value: unknown): value is HTMLSelectElement => {
  if (!isHtmlElement(value)) {
    return false
  }

  const elementWindow = getElementWindow(value)
  return value instanceof elementWindow.HTMLSelectElement
}

const isHtmlButtonElement = (value: unknown): value is HTMLButtonElement => {
  if (!isHtmlElement(value)) {
    return false
  }

  const elementWindow = getElementWindow(value)
  return value instanceof elementWindow.HTMLButtonElement
}

const isVisible = (element: HTMLElement) => {
  const computedStyle = getElementWindow(element).getComputedStyle(element)
  return computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden'
}

const cleanText = (value: string | null | undefined) => {
  return (value ?? '').replace(/\s+/g, ' ').trim()
}

const formatIdentifierAsLabel = (value: string) => {
  return value
    .replace(/^(?:textbox|checkbox|select|signature|text|input)[:_]?/i, '')
    .replace(/[._:-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const getReadableSignatureLabel = (element: HTMLElement, signatureIndex: number) => {
  const rawIdentifier = cleanText(
    element.getAttribute('data-guided-label') ||
      element.getAttribute('blt-tag') ||
      element.getAttribute('id') ||
      element.getAttribute('name')
  )

  const indexedMatch = rawIdentifier.match(/(?:^|[:._-])(\d+)$/)
  if (indexedMatch) {
    return `Signature ${indexedMatch[1]}`
  }

  return `Signature ${signatureIndex}`
}

const escapeCssValue = (value: string) => {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value)
  }

  return value.replace(/["\\]/g, '\\$&')
}

const getElementLabel = (element: HTMLElement) => {
  const ariaLabel = cleanText(element.getAttribute('aria-label'))
  if (ariaLabel) {
    return ariaLabel
  }

  if (element.id) {
    const label = element.ownerDocument.querySelector(`label[for="${escapeCssValue(element.id)}"]`)
    const labelText = cleanText(label?.textContent)
    if (labelText) {
      return labelText
    }
  }

  const wrappingLabelText = cleanText(element.closest('label')?.textContent)
  if (wrappingLabelText) {
    return wrappingLabelText
  }

  const title = cleanText(element.getAttribute('title'))
  if (title) {
    return title
  }

  const name = cleanText(element.getAttribute('name'))
  if (name) {
    return formatIdentifierAsLabel(name)
  }

  const placeholder = cleanText(element.getAttribute('placeholder'))
  if (placeholder) {
    return placeholder
  }

  const id = cleanText(element.getAttribute('id'))
  if (id) {
    return formatIdentifierAsLabel(id)
  }

  return 'Required field'
}

const isStepComplete = (element: HTMLElement, type: GuidedStepType) => {
  if (type === 'signature') {
    return element.getAttribute('signed') === 'true'
  }

  if (type === 'checkbox' && isHtmlInputElement(element)) {
    return element.checked
  }

  if (type === 'select' && isHtmlSelectElement(element)) {
    return cleanText(element.value).length > 0
  }

  if (isHtmlInputElement(element) || isHtmlTextAreaElement(element)) {
    return cleanText(element.value).length > 0
  }

  return false
}

const getStepType = (element: HTMLElement): GuidedStepType => {
  if (element.matches(GUIDED_SIGNATURE_SELECTOR)) {
    return 'signature'
  }

  if (isHtmlSelectElement(element)) {
    return 'select'
  }

  if (isHtmlInputElement(element) && ['checkbox', 'radio'].includes(element.type)) {
    return 'checkbox'
  }

  return 'text'
}

const collectSteps = (iframeDoc: Document) => {
  const elements = Array.from(iframeDoc.querySelectorAll(GUIDED_STEP_SELECTOR)).filter((element) => {
    if (!isHtmlElement(element) || !isVisible(element)) {
      return false
    }

    if (element.matches(GUIDED_SIGNATURE_SELECTOR)) {
      if (
        element.getAttribute('data-guided-required') === 'false' ||
        element.getAttribute('aria-required') === 'false'
      ) {
        return false
      }

      const button = element.querySelector('button')
      if (button && button.disabled) {
        return false
      }
    }

    return true
  }) as HTMLElement[]

  const signatureElements = elements.filter((element) => element.matches(GUIDED_SIGNATURE_SELECTOR))
  const signatureCount = signatureElements.length
  let signatureIndex = 0

  return elements.map((element) => {
    const type = getStepType(element)
    let label = getElementLabel(element)

    if (type === 'signature') {
      signatureIndex += 1
      label = signatureCount > 1 ? getReadableSignatureLabel(element, signatureIndex) : 'Signature'
    }

    return {
      element,
      label,
      type,
      complete: isStepComplete(element, type)
    } as GuidedStep
  })
}

const setStepHighlight = (step: GuidedStep, highlighted: boolean) => {
  const element = step.element
  const previousBackground = element.getAttribute('data-guided-prev-background-color')
  if (previousBackground === null) {
    element.setAttribute('data-guided-prev-background-color', element.style.backgroundColor ?? '')
  }

  const previousBoxShadow = element.getAttribute('data-guided-prev-box-shadow')
  if (previousBoxShadow === null) {
    element.setAttribute('data-guided-prev-box-shadow', element.style.boxShadow ?? '')
  }

  const previousOutline = element.getAttribute('data-guided-prev-outline')
  if (previousOutline === null) {
    element.setAttribute('data-guided-prev-outline', element.style.outline ?? '')
  }

  const previousOutlineOffset = element.getAttribute('data-guided-prev-outline-offset')
  if (previousOutlineOffset === null) {
    element.setAttribute('data-guided-prev-outline-offset', element.style.outlineOffset ?? '')
  }

  if (highlighted) {
    element.style.backgroundColor = GUIDED_HIGHLIGHT_COLOR

    if (step.type === 'checkbox') {
      element.style.boxShadow = GUIDED_CHECKBOX_GLOW
      element.style.outline = '1px solid rgba(255, 200, 0, 0.75)'
      element.style.outlineOffset = '1px'
    }

    return
  }

  element.style.backgroundColor = previousBackground ?? ''
  element.style.boxShadow = previousBoxShadow ?? ''
  element.style.outline = previousOutline ?? ''
  element.style.outlineOffset = previousOutlineOffset ?? ''
}

const focusStep = (step: GuidedStep) => {
  step.element.scrollIntoView({behavior: 'smooth', block: 'center'})

  if (isHtmlInputElement(step.element) || isHtmlTextAreaElement(step.element) || isHtmlSelectElement(step.element)) {
    step.element.focus()
    return
  }

  const signatureButton = step.element.querySelector('button')
  if (isHtmlButtonElement(signatureButton)) {
    signatureButton.focus()
  }
}

const getStateFromSteps = (steps: GuidedStep[]): GuidedDisclosureState => {
  const totalSteps = steps.length
  const completedSteps = steps.filter((step) => step.complete).length
  const firstIncompleteIndex = steps.findIndex((step) => !step.complete)
  const complete = totalSteps === 0 || completedSteps === totalSteps

  return {
    enabled: totalSteps > 0,
    complete,
    totalSteps,
    completedSteps,
    currentStepNumber: firstIncompleteIndex === -1 ? 0 : firstIncompleteIndex + 1,
    currentStepLabel: firstIncompleteIndex === -1 ? '' : steps[firstIncompleteIndex].label
  }
}

const emitState = (iframe: HTMLIFrameElement, state: GuidedDisclosureState) => {
  iframe.dispatchEvent(new CustomEvent<GuidedDisclosureState>(GUIDED_DISCLOSURE_EVENT, {detail: state}))
}

export const createGuidedDisclosureController = (
  iframe: HTMLIFrameElement,
  iframeDoc: Document,
  isEditable: boolean
): GuidedDisclosureController => {
  let destroyed = false
  let steps = [] as GuidedStep[]
  let state = {
    enabled: false,
    complete: true,
    totalSteps: 0,
    completedSteps: 0,
    currentStepNumber: 0,
    currentStepLabel: ''
  } as GuidedDisclosureState
  let raf = 0
  let pendingRecalculate = false

  const clearHighlights = () => {
    steps.forEach((step) => {
      setStepHighlight(step, false)
    })
  }

  const recalculate = () => {
    if (destroyed) {
      return
    }

    clearHighlights()
    steps = isEditable ? collectSteps(iframeDoc) : []
    state = getStateFromSteps(steps)

    if (isEditable && state.enabled && !state.complete) {
      const firstIncompleteStep = steps[state.currentStepNumber - 1]
      if (firstIncompleteStep) {
        setStepHighlight(firstIncompleteStep, true)
      }
    }

    emitState(iframe, state)
  }

  const scheduleRecalculate = () => {
    if (raf) {
      pendingRecalculate = true
      return
    }

    raf = window.requestAnimationFrame(() => {
      recalculate()
      raf = 0

      if (pendingRecalculate) {
        pendingRecalculate = false
        scheduleRecalculate()
      }
    })
  }

  const handleFieldInput = (event: Event) => {
    const eventTarget = event.target
    if (!isHtmlElement(eventTarget)) {
      return
    }

    if (!eventTarget.matches(GUIDED_REQUIRED_SELECTOR) && !eventTarget.closest(GUIDED_SIGNATURE_SELECTOR)) {
      return
    }

    scheduleRecalculate()
  }

  iframeDoc.addEventListener('change', handleFieldInput, true)
  iframeDoc.addEventListener('input', handleFieldInput, true)
  iframeDoc.addEventListener('blur', handleFieldInput, true)

  const observer = new MutationObserver(() => {
    scheduleRecalculate()
  })

  if (iframeDoc.body) {
    observer.observe(iframeDoc.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['signed', 'required', 'aria-required', 'value', 'disabled']
    })
  }

  recalculate()

  return {
    destroy: () => {
      if (destroyed) {
        return
      }

      destroyed = true
      if (raf) {
        window.cancelAnimationFrame(raf)
        raf = 0
      }
      pendingRecalculate = false
      observer.disconnect()
      iframeDoc.removeEventListener('change', handleFieldInput, true)
      iframeDoc.removeEventListener('input', handleFieldInput, true)
      iframeDoc.removeEventListener('blur', handleFieldInput, true)
      clearHighlights()
    },
    goToNextRequired: () => {
      if (!isEditable) {
        return
      }

      const firstIncompleteStep = steps.find((step) => !step.complete)
      if (!firstIncompleteStep) {
        return
      }

      focusStep(firstIncompleteStep)
      scheduleRecalculate()
    },
    getState: () => state
  }
}